// status-ui.jsx — connection-health overlays for the standalone PWA.
//
// Two small fixed-position surfaces rendered above <App>:
//
//   <ReconnectingPill/> — top-centre pill while the WebSocket is down.
//     Appearance is delayed ~600ms via CSS animation so the sub-second
//     reconnects that happen on every LAN blip never flash it.
//
//   <ServiceErrorToast/> — bottom-centre toast when a service call
//     rejects (a press that didn't go through). Tap to dismiss;
//     auto-dismisses from the hook after a few seconds.
//
// Colours come from theme.js, which is plain constants with no imports,
// rather than from the app's live palette — these overlays must render
// even when the app layer is unhappy.

import * as React from 'react';
import { LIGHT, DARK, FONT } from '../theme.js';

const INK = LIGHT.ink;
const PAPER = LIGHT.paper;
const MUTE_ON_INK = DARK.inkSoft;
const SANS = FONT.sans;

const css = `
@keyframes pwa-status-appear {
  from { opacity: 0; transform: translate(-50%, -6px); }
  to   { opacity: 1; transform: translate(-50%, 0); }
}
@keyframes pwa-toast-appear {
  from { opacity: 0; transform: translate(-50%, 10px); }
  to   { opacity: 1; transform: translate(-50%, 0); }
}
@keyframes pwa-status-pulse {
  0%, 100% { opacity: .35; }
  50%      { opacity: 1; }
}
`;

export function ReconnectingPill({ connState }) {
  if (connState !== 'reconnecting') return null;
  return (
    <>
      <style>{css}</style>
      <div
        style={{
          position: 'fixed',
          top: 'calc(env(safe-area-inset-top, 0px) + 10px)',
          left: '50%',
          zIndex: 300,
          display: 'flex',
          alignItems: 'center',
          gap: 8,
          padding: '10px 18px',
          borderRadius: 14,
          background: INK,
          color: PAPER,
          fontFamily: SANS,
          fontSize: 15,
          fontWeight: 500,
          boxShadow: '0 4px 14px rgba(34,33,32,.28)',
          pointerEvents: 'none',
          // Delayed + "both" so quick reconnects never flash the pill.
          animation: 'pwa-status-appear .25s ease .6s both',
        }}
      >
        <span
          style={{
            width: 6,
            height: 6,
            borderRadius: '50%',
            background: PAPER,
            animation: 'pwa-status-pulse 1.2s ease-in-out infinite',
          }}
        />
        Reconnecting
      </div>
    </>
  );
}

export function ServiceErrorToast({ serviceError, onDismiss }) {
  if (!serviceError) return null;
  return (
    <>
      <style>{css}</style>
      <div
        key={serviceError.ts}
        onClick={onDismiss}
        style={{
          position: 'fixed',
          bottom: 'calc(env(safe-area-inset-bottom, 0px) + 18px)',
          left: '50%',
          zIndex: 310,
          maxWidth: 'min(440px, calc(100vw - 32px))',
          padding: '12px 18px',
          borderRadius: 14,
          background: INK,
          color: PAPER,
          fontFamily: SANS,
          boxShadow: '0 6px 20px rgba(34,33,32,.32)',
          cursor: 'pointer',
          animation: 'pwa-toast-appear .22s ease both',
        }}
      >
        <div style={{ fontSize: 15, fontWeight: 500, lineHeight: 1.45 }}>{serviceError.message}</div>
        {serviceError.detail && (
          <div
            style={{
              marginTop: 4,
              fontSize: 13,
              color: MUTE_ON_INK,
            }}
          >
            {serviceError.detail}
          </div>
        )}
      </div>
    </>
  );
}
